import { createApp } from 'vue'
import App from './App.vue'
import router from './router';
import { clear_auth_state, csrf_headers, is_demo_domain, is_valid_csrf_token } from './router';
import '@fortawesome/fontawesome-free/css/all.css';
import NotyfPlugin from './plugins/notyf'; // if you created a plugin wrapper
import branding_plugin from './plugins/branding_plugin';
import branding_service from './services/branding_service';
import {
  shutdown_push_notifications,
  sync_push_notifications_for_auth_state,
} from './services/push_notification_service';
import { install_page_loader_restore_handlers } from './utils/page_loader';
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import $ from 'jquery';


// ... (keep your imports at the top)

const app = createApp(App);
app.use(router);
app.use(NotyfPlugin);
app.use(branding_plugin);
install_page_loader_restore_handlers();

let is_auth_redirect_in_progress = false;

function get_auth_error_message(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  return String(error.message || error.detail || error.statusText || '');
}

function is_auth_error(error) {
  if (error?.__auth_redirect__ === true) return true;
  const message = get_auth_error_message(error);
  return /unauthorized|forbidden/i.test(message);
}

export function handle_unauthorized_session() {
  if (is_demo_domain()) return;
  if (is_auth_redirect_in_progress) return;

  is_auth_redirect_in_progress = true;
  shutdown_push_notifications('unauthorized');
  clear_auth_state();
  localStorage.removeItem('user');
  localStorage.removeItem('is_admin');
  localStorage.removeItem('sso_next');

  router.replace({ name: 'Login' }).catch(() => undefined).finally(() => {
    window.setTimeout(() => {
      is_auth_redirect_in_progress = false;
    }, 250);
  });
}

function sanitize_fetch_csrf_headers(args) {
  const [resource, init] = args;
  if (!init?.headers) {
    return args;
  }

  const headers = new Headers(init.headers);
  const csrf_header = headers.get('X-CSRFToken') || headers.get('X-CSRF-Token');

  if (csrf_header && !is_valid_csrf_token(csrf_header)) {
    headers.delete('X-CSRFToken');
    headers.delete('X-CSRF-Token');
    Object.entries(csrf_headers()).forEach(([key, value]) => headers.set(key, value));
  }

  return [resource, { ...init, headers }];
}

const original_fetch = window.fetch.bind(window);
window.fetch = async (...args) => {
  const response = await original_fetch(...sanitize_fetch_csrf_headers(args));

  if (response.status === 401) {
    handle_unauthorized_session();
  }

  return response;
};

$(document).ajaxError((_event, jqxhr) => {
  if (jqxhr?.status === 401) {
    handle_unauthorized_session();
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (!is_auth_error(event.reason)) return;

  event.preventDefault();
  handle_unauthorized_session();
});

app.config.errorHandler = (error) => {
  if (is_auth_error(error)) {
    handle_unauthorized_session();
    return;
  }

  console.error(error);
};

// Initialize branding on route changes
router.beforeEach(async (to, from, next) => {
  if (!branding_service.initialized) {
    try {
      const user_details = localStorage.getItem('user');
      if (user_details) {
        const user = JSON.parse(user_details);
        const org_id = user?.company_id || user?.organization_id;
        if (org_id) {
          await branding_service.load_branding(org_id);
        }
      }
    } catch (error) {
      console.error('[Branding] Failed to load:', error);
    }
  }

  // Save the current logo to localStorage so index.html can use it next load
  const styleConfig = get_domain_style();
  if (styleConfig.loader) {
    localStorage.setItem('loader_logo', styleConfig.loader);
  }

  next();
});

router.afterEach(() => {
  sync_push_notifications_for_auth_state();
});

/**
 * This function now returns the object and handles
 * the data-client attribute properly.
 */
export function get_domain_style() {
  const domain = process.env.VUE_APP_DOMAIN;

  if (domain) {
    document.documentElement.setAttribute('data-client', domain);
  }

  // Default values
  let styleConfig = {
    name: 'aic-one',
    style: import('@/views/home.css'),
    login_page_logo: require('@/assets/aicone_logo.svg'),
    title: 'AIC ONE',
    logo: require('@/assets/aicone_logo_v3.svg'),
    favicon: require('@/assets/aicone_logo_v3.svg'),
    reset_logo: require('@/assets/aicone_logo.svg'),
    loader: require('@/assets/aicone_logo.svg'),
    //Sidebar Icons images
    collapse_vector_sidebar: require('@/assets/sidebar_toggle.svg'),
    settings_vector_sidebar: require('@/assets/setting.svg'),
    profile_vector_sidebar: require('@/assets/profile.svg'),
    users_vector_sidebar: require('@/assets/user.svg'),
    workspace_vector_sidebar: require('@/assets/empty-wallet.svg'),
    permissions_vector_sidebar: require('@/assets/permission.svg'),
    audit_vector_sidebar: require('@/assets/safearea.svg'),
    app_vector: require('@/assets/aicone_platforms.svg'),
    notification_vector: require('@/assets/Notification.svg'),
    // ... add other default assets here using require()
  };

  if (domain.includes('rematholdings')) {
    styleConfig = {
      ...styleConfig,
      name: 'rematholdings',
      style: import('@/views/home.css'),
      title: 'Remat Holdings',
      login_page_logo: require('@/assets/remat_holdings/remat_logo.png'),
      logo: require('@/assets/remat_holdings/remat_logo.png'),
      favicon: require('@/assets/remat_holdings/remat_logo.png'),
      reset_logo: require('@/assets/remat_holdings/remat_logo.png'),
      loader: require('@/assets/remat_holdings/remat_logo.png'),
      //Sidebar Icons images
      settings_vector_sidebar: require('@/assets/remat_holdings/settings_vector.svg'),
      profile_vector_sidebar: require('@/assets/remat_holdings/profile_vector.svg'),
      users_vector_sidebar: require('@/assets/remat_holdings/users_vector.svg'),
      workspace_vector_sidebar: require('@/assets/remat_holdings/workspace_vector.svg'),
      permissions_vector_sidebar: require('@/assets/remat_holdings/permissions_vector.svg'),
      audit_vector_sidebar: require('@/assets/remat_holdings/audit_vector.svg'),
      app_vector: require('@/assets/remat_holdings/app_vector.svg'),
      notification_vector: require('@/assets/remat_holdings/notification_vector.svg'),
    };
  }

  if (domain.includes('vettedsa')) {
    styleConfig = {
      ...styleConfig,
      name: 'vettedsa',
      style: import('@/views/home.css'),
      title: 'Vetted SA',
      login_page_logo: require('@/assets/vettedsa/vetted_sa.png'),
      logo: require('@/assets/vettedsa/vetted_sa.png'),
      favicon: require('@/assets/vettedsa/vetted_favicon.png'),
      reset_logo: require('@/assets/vettedsa/vetted_sa.png'),
      loader: require('@/assets/vettedsa/vetted_sa_white.png'),

      //Sidebar Icons images
      settings_vector_sidebar: require('@/assets/remat_holdings/settings_vector.svg'),
      profile_vector_sidebar: require('@/assets/remat_holdings/profile_vector.svg'),
      users_vector_sidebar: require('@/assets/remat_holdings/users_vector.svg'),
      workspace_vector_sidebar: require('@/assets/remat_holdings/workspace_vector.svg'),
      permissions_vector_sidebar: require('@/assets/remat_holdings/permissions_vector.svg'),
      audit_vector_sidebar: require('@/assets/remat_holdings/audit_vector.svg'),
      app_vector: require('@/assets/remat_holdings/app_vector.svg'),
      notification_vector: require('@/assets/Notification.svg'),
    };
  }

// --- START INJECTION BLOCK ---

  // 1. Inject the Page Title
  document.title = styleConfig.title;

  // 2. Inject the Favicon
  // This looks for <link rel="icon"> or <link rel="shortcut icon">
  let favicon = document.querySelector('link[rel="icon"]') ||
                document.querySelector('link[rel="shortcut icon"]');

  if (favicon) {
    favicon.href = styleConfig.favicon;
  } else {
    // If for some reason the tag is missing, create it
    const new_link = document.createElement('link');
    new_link.rel = 'icon';
    new_link.href = styleConfig.favicon;
    document.head.appendChild(new_link);
  }

  // 3. Inject the Apple Touch Icon (optional, for mobile)
  let apple_icon = document.querySelector('link[rel="apple-touch-icon"]');
  if (apple_icon) {
    apple_icon.href = styleConfig.favicon;
  }

  return styleConfig;
}

// Call the function so it executes
const currentStyle = get_domain_style();
app.config.globalProperties.$domainStyle = currentStyle;

app.mount('#app');
sync_push_notifications_for_auth_state();
export default app;
